import React, { useContext, useCallback } from 'react';
import { Route, Redirect, useLocation } from 'react-router-dom';
import { useRecoilValue } from 'recoil';

import useDependencies from '@app/hooks/useDependencies';
import { FlexColumn } from '@shared/ui/Containers/FlexContainer';
import Loader from '@ui/Utility/Loader';
import { AuthContext } from '@app/providers/firebase/AuthProvider';
import { Permission } from '@shared/permissions';
import { usePermissions } from '@shared/permissions/usePermissions';

interface IComponent {
  exact: boolean;
  path: string;
  deps: string[];
  children: React.ReactNode;
  /**
   * Required permissions for this route.
   * User must have at least ONE of these permissions to access the route.
   * Use this for the new permission-based access control.
   */
  requiredPermissions?: Permission[];
  /**
   * If true, user must have ALL required permissions (not just one).
   * Default is false (user needs any one of the permissions).
   */
  requireAll?: boolean;
  /**
   * @deprecated Use `requiredPermissions` instead.
   * Legacy whitelist of email addresses.
   * Kept for backwards compatibility during migration.
   */
  whitelist?: string[];
}

/**
 * PrivateRouteWithDeps - Route component that requires authentication and dependencies.
 * 
 * This component supports both the new permission-based system and the legacy
 * whitelist pattern for backwards compatibility during migration.
 * 
 * @example New permission-based usage:
 * ```tsx
 * <PrivateRouteWithDeps
 *   path="/orders"
 *   exact
 *   deps={['customers']}
 *   requiredPermissions={['orders.view']}
 * >
 *   <OrdersList />
 * </PrivateRouteWithDeps>
 * ```
 * 
 * @example Legacy whitelist usage (deprecated):
 * ```tsx
 * <PrivateRouteWithDeps
 *   path="/orders"
 *   exact
 *   deps={['customers']}
 *   whitelist={[...adminEmails, ...shopEmails]}
 * >
 *   <OrdersList />
 * </PrivateRouteWithDeps>
 * ```
 */
const PrivateRouteWithDeps = ({
  exact,
  path,
  deps,
  children,
  requiredPermissions,
  requireAll = false,
  whitelist = [],
}: IComponent) => {
  const location = useLocation();
  const depsReady = useDependencies(deps);
  const { currentUser } = useContext(AuthContext);
  const { hasPermission, hasAllPermissions, defaultPage } = usePermissions();



  /**
   * Determines the redirect URL for a user who doesn't have access.
   * Uses the new permission system's defaultPage if available,
   * otherwise falls back to legacy logic.
   */
  const redirectUrlFor = useCallback((userEmail: string | null | undefined) => {
    // If using new permission system, use the configured default page
    if (requiredPermissions && requiredPermissions.length > 0) {
      return defaultPage;
    }
    // Legacy fallback — admin/shop/scanner atoms still used by legacy routes
    return '/login';
  }, [
    requiredPermissions,
    defaultPage,
  ]);

  /**
   * Determines if the current user has access to this route.
   */
  const userHasAccess = useCallback((): boolean => {
    if (!currentUser) return false;

    // If using new permission system
    if (requiredPermissions && requiredPermissions.length > 0) {
      return requireAll
        ? hasAllPermissions(requiredPermissions)
        : hasPermission(requiredPermissions);
    }

    // Legacy whitelist check
    return whitelist.length === 0 || includes(whitelist, currentUser.email);
  }, [
    currentUser,
    requiredPermissions,
    requireAll,
    hasPermission,
    hasAllPermissions,
    whitelist,
  ]);

  // Not authenticated at all
  if (!currentUser) {
    return <Redirect to="/login" />;
  }

  // User has access
  if (userHasAccess()) {
    return (
      <>
        {!depsReady() ? (
          <FlexColumn style={{ width: '100%', height: '50vw' }}>
            <Loader />
          </FlexColumn>
        ) : (
          <Route
            exact={exact}
            path={path}
            render={() => children}
          />
        )}
      </>
    );
  }

  // User doesn't have access, redirect to appropriate page
  return <Redirect to={redirectUrlFor(currentUser?.email)} />;
};

export default PrivateRouteWithDeps;
